import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledField } from '@shared/components/ControlledField';
import { FormDialog } from '@shared/components/dialogs/FormDialog';
import { DEFAULT_CHANGE_TEMPLATE_ASSESSMENT_FORM, changeTemplateAssessmentFormSchema } from './changeTemplateForm';
import type { TChangeTemplateAssessmentFormValues } from '../types';

type TProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onCreate: (values: TChangeTemplateAssessmentFormValues) => Promise<unknown>;
};

const ChangeTemplateAssessmentDialog = ({ isOpen, setIsOpen, onCreate }: TProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TChangeTemplateAssessmentFormValues>({
    resolver: yupResolver(changeTemplateAssessmentFormSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_CHANGE_TEMPLATE_ASSESSMENT_FORM,
  });

  useEffect(() => {
    if (isOpen) reset(DEFAULT_CHANGE_TEMPLATE_ASSESSMENT_FORM);
  }, [isOpen]);

  const onSubmit = async (values: TChangeTemplateAssessmentFormValues) => {
    try {
      await onCreate(values);
      setIsOpen(false);
    } catch {
      /* The API hook reports the failure. Preserve the form for retry. */
    }
  };

  return (
    <FormDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Add assessment"
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Add"
      submitIcon={null}
      isSubmitting={isSubmitting}
    >
      <div className="flex flex-col gap-4">
        <ControlledField
          inputType="input"
          name="title"
          label="Title"
          placeholder="Enter assessment title"
          required
          control={control}
          errors={errors}
        />
        <ControlledField inputType="checkbox" name="required" label="Required" control={control} errors={errors} />
      </div>
    </FormDialog>
  );
};

export default ChangeTemplateAssessmentDialog;
